import dynamic from 'next/dynamic';
import styled from 'styled-components';
import isEqual from 'lodash/isEqual';
import { useEffect, useState } from 'react';

import { LocalizedCurrency } from 'shared/components/LocalizedCurrency';
import { DEFAULT_CURRENCY_CODE } from 'shared/enums/SitePreferences';
import { getInitialRangeState } from 'search/utils/getInitialRangeStateFromFacet';
import { IConstructorFacetRange } from 'shared/types/ConstructorFacet';
import {
  convertPriceAndRoundDown,
  convertPriceAndRoundUp,
} from 'search/utils/convertPriceAndRound';
import { useCurrency } from 'shared/hooks/useCurrency';

const Slider = dynamic(() => import('shared/components/Slider').then((mod) => mod.Slider), {
  ssr: false,
});

export interface ISearchFilterPriceMobile {
  facet: IConstructorFacetRange;
  numColumns: number;
  selectedFilterValues: string;
  setSelectedFilterValues: (selectedFilterValues: string) => void;
}

export const SearchFilterPriceMobile = ({
  facet,
  selectedFilterValues,
  setSelectedFilterValues,
}: ISearchFilterPriceMobile) => {
  const { data, min, max } = facet;
  const { selectedCurrencyCode } = useCurrency();
  const currencyRates = data?.currency_rates;
  const selectedCurrencyRate = currencyRates?.[selectedCurrencyCode];
  const currencyConversionRate =
    selectedCurrencyCode !== DEFAULT_CURRENCY_CODE && selectedCurrencyRate
      ? selectedCurrencyRate
      : 1;

  const range: [number, number] = [min, max];
  const initialState = getInitialRangeState(selectedFilterValues);
  const [values, setValues] = useState(initialState || range);
  const [update, setUpdate] = useState(initialState || range);

  const handleUpdate = (updatedValues) => {
    setUpdate(updatedValues);
  };

  const handleSlideEnd = (updatedValues: [number, number]) => {
    setValues(updatedValues);
    setSelectedFilterValues(`${updatedValues[0]}-${updatedValues[1]}`);
  };

  useEffect(() => {
    // BUY3-2341: when we switch from a different filter (ex: size, condition, etc.)
    // and the state has not updated until the 2nd re-render,
    // we need to force the sliders to update when the selectedFilterValues update
    if (selectedFilterValues.length > 1 && !isEqual(values, initialState)) {
      setValues(initialState);
      setUpdate(initialState);
    }
  }, [selectedFilterValues]);

  return (
    <>
      <SearchFilterPriceMobile.YearSliderKeys data-qa="price_slider_range_state">
        <span data-qa="price_slider_min_value">
          <LocalizedCurrency price={convertPriceAndRoundDown(update[0], currencyConversionRate)} />
        </span>
        <span data-qa="price_slider_max_value">
          <LocalizedCurrency price={convertPriceAndRoundUp(update[1], currencyConversionRate)} />
        </span>
      </SearchFilterPriceMobile.YearSliderKeys>
      <SearchFilterPriceMobile.PriceSliderWrapper>
        <Slider
          data-qa="price_filter_slider"
          range={range}
          handleSlideEnd={handleSlideEnd}
          handleUpdate={handleUpdate}
          step={100}
          values={values}
        />
      </SearchFilterPriceMobile.PriceSliderWrapper>
    </>
  );
};

SearchFilterPriceMobile.YearSliderKeys = styled.div`
  display: flex;
  justify-content: space-between;
`;

SearchFilterPriceMobile.PriceSliderWrapper = styled.div`
  margin: 24px 8px;
`;
